import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Box, InputLabel, Stack } from "@mui/material";
import { format, parse, isValid } from "date-fns";
import { logError } from "~src/utils/logger";
const DateRangeSelector = ({
  value,
  onSelect,
}: {
  value?: string;
  onSelect: (value?: string) => void;
}) => {
  // Parse dates safely with validation
  const parseSafeDate = (dateStr: string) => {
    try {
      const parsedDate = parse(dateStr, "yyyy-MM-dd", new Date());
      return isValid(parsedDate) ? parsedDate : null;
    } catch (error) {
      logError(`Error parsing date: ${error}`);
      return null;
    }
  };

  // Safely parse start and end dates
  const getDateRange = () => {
    if (!value) return [null, null];
    
    try {
      const [startDateStr, endDateStr] = value.split("__");
      const startDate = parseSafeDate(startDateStr);
      const endDate = parseSafeDate(endDateStr);
      return [startDate, endDate];
    } catch (error) {
      logError(`Error parsing date range: ${error}`);
      return [null, null];
    }
  };

  const [startDate, endDate] = getDateRange();

  const handleStartDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    
    try {
      const formattedStartDate = format(date, "yyyy-MM-dd");
      const formattedEndDate = endDate && isValid(endDate) 
        ? format(endDate, "yyyy-MM-dd") 
        : format(new Date(), "yyyy-MM-dd");
      
      const formattedValue = `${formattedStartDate}__${formattedEndDate}`;
      onSelect(formattedValue);
    } catch (error) {
      logError(`Error formatting start date: ${error}`);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    
    try {
      const formattedStartDate = startDate && isValid(startDate) 
        ? format(startDate, "yyyy-MM-dd") 
        : format(new Date(), "yyyy-MM-dd");
      
      const formattedEndDate = format(date, "yyyy-MM-dd");
      const formattedValue = `${formattedStartDate}__${formattedEndDate}`;
      onSelect(formattedValue);
    } catch (error) {
      logError(`Error formatting end date: ${error}`);
    }
  };

  return (
    <Box>
      <InputLabel sx={{ fontWeight: "bold", pb: 2 }}>
        Select Date Range
      </InputLabel>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="row" spacing={2}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            disableFuture
            format="yyyy-MM-dd"
            slotProps={{
              textField: { fullWidth: true }
            }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            disableFuture
            format="yyyy-MM-dd"
            minDate={startDate || undefined}
            slotProps={{
              textField: { fullWidth: true }
            }}
          />
        </Stack>
      </LocalizationProvider>
    </Box>
  );
};

export default DateRangeSelector;
